import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { useHydrated } from 'remix-utils';
import {
    MagicIsland,
    MagicIslandProvider,
} from '~/ui-rocco/pages/detail-shared';
import { useLocalisation } from '@archipro-website/localisation';
import { formatDateToDDMonthYYYY } from '@rocco/utils/format-date';
import { EditorialArticleGrid } from './EditorialArticleGrid';
import { EditorialDirectoryHero } from './EditorialDirectoryHero';
import { TrendingCategories } from './TrendingCategories';
import { EditorialIntroStrip } from './EditorialIntroStrip';
import { EditorialSectionHeader } from './EditorialSectionHeader';
import { MagazinePromoBand } from './MagazinePromoBand';
import { EditorialViewMore } from './EditorialViewMore';
import { EDITORIAL_PAGE_SIZE } from '../config/paging';
import { useDirectoryViewMore } from '~/ui-rocco/pages/category-page/hooks/useProductViewMore';
import { ARTICLES_LOAD_MORE_API_ROUTE } from '~/routes/remix-api.articles.load-more';
import type { FeaturedCarouselSlide } from './FeaturedFilmCarousel';
import { FeaturedFilmCarousel } from './FeaturedFilmCarousel';
import type { PremiumArticleFeature } from './PremiumArticleModule';
import { PremiumArticleModule } from './PremiumArticleModule';
import type { CategoryNavItem } from '../util/derive-category-rail';
import type { ArchizeenTile } from '../util/map-archizeen-tile-to-editorial';
import { mapArchizeenTileToEditorial } from '../util/map-archizeen-tile-to-editorial';
import type { RevampCategoryChip } from '../type';
import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import { ROOT_CATEGORY_LINK } from '@modules/articles/config/constants';
import { introStripCopy, promoBandBody } from '../util/editorial-copy';

/**
 * The masthead article. Sourced from the directory's featured slides rather
 * than the plain tile list: only that shape carries the article's intro text
 * and its full-bleed banner image, both of which the design needs.
 */
export interface EditorialHeroArticle {
    title: string;
    link: string;
    content?: string;
    categoryLabel?: string;
    date?: string;
    image?: { src?: string };
    /** Control label, where a curated placement overrides the default. */
    ctaLabel?: string;
}

interface ArticlesDirectoryRevampProps {
    /** The article promoted in the masthead. */
    hero?: EditorialHeroArticle;
    /** Slides for the featured article/film carousel. */
    featured: FeaturedCarouselSlide[];
    /** The article promoted in the interruptive module between grid blocks. */
    premium?: PremiumArticleFeature;
    /**
     * The root listing, as served — page one plus a small spare. Still carries
     * the masthead and premium articles; the grid drops them below rather than
     * the loader, so the one rule lives with the sections that promote them.
     */
    articles: ArchizeenTile[];
    /** Total across all pages, so the grid knows when to stop paging. */
    totalCount: number;
    /**
     * Articles the page promotes above the grid — the masthead, the carousel
     * slides and the premium feature. Resolved by the loader, which sizes the
     * listing read against the same list.
     */
    excludeLinks: string[];
    navItems: CategoryNavItem[];
}

/**
 * Where the grid breaks for the injected sections, in cards. The design runs
 * four rows, then the promo band, then a section header and two more rows,
 * then the premium feature, then the rest.
 */
const CARDS_BEFORE_PROMO = 16;
const CARDS_BEFORE_FEATURE = 24;

/**
 * Design-length copy for the promo band. The frame sets a 68-character headline
 * across three lines of a 655 column and a ~155-character body across three
 * lines of a 621 column; shorter copy leaves the composition looking empty.
 * Deliberately market-neutral — this page serves both NZ and AU.
 */
const PROMO_HEADLINE =
    "Subscribe to our Digital Publications and see what's shaping design";

const toChips = (navItems: CategoryNavItem[]): RevampCategoryChip[] =>
    navItems
        .filter((item) => item.link !== ROOT_CATEGORY_LINK)
        .map((item, index) => ({
            id: index,
            title: item.title,
            link: item.link,
        }));

/**
 * Composes the revamped /articles directory landing page.
 *
 * Section rhythm follows the Figma frame (60:1948): a 100px spacer under the
 * masthead and 65px between the Trending Categories block and the grid.
 */
export const ArticlesDirectoryRevamp = ({
    hero,
    featured,
    premium,
    articles,
    totalCount,
    excludeLinks,
    navItems,
}: ArticlesDirectoryRevampProps) => {
    const { country } = useLocalisation();
    const categories = useMemo(() => toChips(navItems), [navItems]);

    const { initialTiles, offsetBase } = useMemo(() => {
        const excluded = new Set(excludeLinks);
        const kept = articles.filter((article) => !excluded.has(article.link));
        return {
            initialTiles: kept.slice(0, EDITORIAL_PAGE_SIZE).map((article) => ({
                model: mapArchizeenTileToEditorial(article, {
                    variant: 'editorialLarge',
                }),
            })),
            /*
             * Rows page one read that it does not show. Paging runs on rows, so
             * the promoted articles it dropped still have to be stepped over —
             * counting only the tiles would re-read their rows on page two.
             */
            offsetBase: articles.length - kept.length,
        };
    }, [articles, excludeLinks]);

    // The facets the later pages have to inherit, and the promoted articles
    // they have to keep dropping. Travels through the hook's `filters` slot,
    // which it forwards to the route verbatim.
    const filters = useMemo(
        () => ({ variant: 'editorialLarge', exclude: excludeLinks }),
        [excludeLinks],
    );

    // Which page the loader served. The Load More anchor is a real `?page=N`
    // URL, so the grid can be entered part way in; without this the next fetch
    // would carry on from row 0 rather than from where that page ended.
    const { search } = useLocation();
    const firstLoadedPage = useMemo(() => {
        const page = Number.parseInt(
            new URLSearchParams(search).get('page') ?? '1',
            10,
        );
        return Number.isFinite(page) && page > 0 ? page : 1;
    }, [search]);

    const {
        products: paged,
        loadMore,
        hasMore,
        isLoading,
        nextPage,
    } = useDirectoryViewMore({
        initialProducts: initialTiles,
        totalCount,
        categoryLink: ROOT_CATEGORY_LINK,
        pageSize: EDITORIAL_PAGE_SIZE,
        offsetBase,
        firstLoadedPage,
        filters,
        actionPath: ARTICLES_LOAD_MORE_API_ROUTE,
    });

    const tiles = useMemo(
        () => paged.map((tile) => tile.model as ArticleTileViewModel),
        [paged],
    );

    const hydrated = useHydrated();

    return (
        <MagicIslandProvider>
            <main
                // overflow-x-clip contains the masthead's full-bleed breakout. Its
                // 100vw counts the vertical scrollbar, which the visible viewport
                // does not, so on any page long enough to scroll the hero overhangs
                // by the scrollbar's width and raises a horizontal scrollbar. The
                // overhang is symmetric, so clipping both sides leaves the media
                // centred and edge to edge. `clip` rather than `hidden`: it does not
                // become a scroll container, so sticky descendants still work.
                className="flex flex-col overflow-x-clip pb-[10rem]"
                data-testid="articles-directory-revamp"
            >
                {hero && (
                    <EditorialDirectoryHero
                        title={hero.title}
                        meta={
                            [
                                hero.categoryLabel,
                                hero.date && formatDateToDDMonthYYYY(hero.date),
                            ]
                                .filter(Boolean)
                                .join(', ') || undefined
                        }
                        standfirst={hero.content || undefined}
                        link={hero.link}
                        imageSrc={hero.image?.src}
                        ctaLabel={hero.ctaLabel}
                    />
                )}

                {/*
                 * Vertical rhythm straight off the Figma frame: 100 to the intro,
                 * 65 to the categories, 65 to the first section header, the header
                 * sits directly on its section, 160 between sections.
                 */}
                <EditorialIntroStrip
                    copy={introStripCopy(country)}
                    className="mt-[6.25rem]"
                />

                <TrendingCategories
                    categories={categories}
                    className="mt-[4.0625rem]"
                />

                <EditorialSectionHeader
                    title="(Featured Article)"
                    className="mt-[4.0625rem]"
                />
                <FeaturedFilmCarousel slides={featured} />

                {/*
                 * 78, not the 160 the other section breaks use: the frame's 160
                 * spacer here holds the carousel's pagination pill, which the
                 * carousel renders itself 40 under the slide. The remainder is
                 * this gap.
                 */}
                <EditorialSectionHeader
                    title="(Latest Articles)"
                    className="mt-[4.875rem]"
                />

                {/* The feature interrupts the grid after two rows, as the design
                does, rather than sitting below it. */}
                <EditorialArticleGrid
                    articles={tiles.slice(0, CARDS_BEFORE_PROMO)}
                    layout="directory"
                />

                <MagazinePromoBand
                    headline={PROMO_HEADLINE}
                    body={promoBandBody(country)}
                    subscribeLink="/digital-magazines"
                    className="mt-[15.625rem]"
                />

                <EditorialSectionHeader
                    title="(More Reading)"
                    className="mt-[10rem]"
                />
                <EditorialArticleGrid
                    articles={tiles.slice(
                        CARDS_BEFORE_PROMO,
                        CARDS_BEFORE_FEATURE,
                    )}
                    layout="directory"
                />

                {premium && (
                    <PremiumArticleModule
                        article={premium}
                        // A curated placement carries the editor's own label and
                        // CTA; the entitlement fallback has neither and keeps the
                        // module's defaults.
                        {...(premium.eyebrow && { eyebrow: premium.eyebrow })}
                        {...(premium.ctaLabel && {
                            ctaLabel: premium.ctaLabel,
                        })}
                        className="mt-[6.25rem]"
                    />
                )}

                <EditorialArticleGrid
                    articles={tiles.slice(CARDS_BEFORE_FEATURE)}
                    layout="directory"
                    className="mt-[10rem]"
                />

                <EditorialViewMore
                    hasMore={hasMore}
                    isLoading={isLoading}
                    onLoadMore={loadMore}
                    nextPage={nextPage}
                    className="mt-[6.25rem]"
                />
            </main>

            {/* Fixed chrome, so it sits outside the page flow. Held back until
                hydration: it is driven entirely by client-side carousel state
                and has nothing to render on the server. */}
            {hydrated ? <MagicIsland /> : null}
        </MagicIslandProvider>
    );
};
